import PropTypes from 'prop-types';
import { VisuallyHidden } from '@figure/frontlib-gui';

/**
 * Sometimes we add or remove content to the page to let a sighted user know that an action was successful or unsuccessful,
 * that something is loading, that they are on a new step in a progress bar, or that an error occurred.
 * This component is used to communicate these types of status messages to blind and low vision users.
 * A status message does not receive focus. For example, a message in a modal is not a status message.
 * This component will only make an announcement if the status message text changes.
 * For additional use case examples, see Success Criterion 4.1.3: Status Messages (https://www.w3.org/WAI/WCAG21/Understanding/status-messages.html).
 * For additional information on live regions, see the MDN ARIA live region docs: https://developer.mozilla.org/en-US/docs/Web/Accessibility/ARIA/ARIA_Live_Regions.
 */

const AriaLive = ({ ariaLive, statusMessage, role, ...rest }) => (
  <VisuallyHidden tag="div" aria-live={ariaLive} role={role} {...rest}>
    {statusMessage}
  </VisuallyHidden>
);

AriaLive.propTypes = {
  /**
   * A `polite` message will wait for a user to pause before announcing a status message.
   * An `assertive` message interrupts the user.
   * Assertive messages should be used sparingly and only for urgent messages.
   */
  ariaLive: PropTypes.oneOf(['polite', 'assertive']),
  /**
   * `status` is equivalent to `aria-live="polite"`
   * `alert` is equivalent to `aria-live="assertive"`
   * We want to have both the `aria-live` and `role` attributes for the broadest possible assistive technology coverage.
   * See the MDN docs for a description of the other specialized roles: https://developer.mozilla.org/en-US/docs/Web/Accessibility/ARIA/ARIA_Live_Regions
   */
  role: PropTypes.oneOf(['log', 'status', 'alert', 'progressbar', 'marquee', 'timer']),
  /**
   * The status message.
   */
  statusMessage: PropTypes.node,
};

AriaLive.defaultProps = {
  ariaLive: 'polite',
  role: 'status',
  statusMessage: '',
};

export default AriaLive;
